import { BaseAPI } from 'src/providers/base-api';

/** Fired on `window` when the stored menu changed or its refresh state changed; the sidebar re-reads both. */
export const MENU_ORDER_CHANGED_EVENT = 'menu-order-changed';

// Set in session storage when a save or reset went through but the sidebar could not be refreshed, so
// the state survives a page reload and the sidebar can say it is out of date until a refresh works.
const REFRESH_PENDING_KEY = 'menuRefreshPending';

// Waits before each retry of a failed fetch (so up to three tries in all).
const RETRY_DELAYS_MS = [1000, 2000];

/** True while the sidebar menu is known to be out of date (the last refresh failed). */
export function isMenuRefreshPending(): boolean {
  try {
    return sessionStorage.getItem(REFRESH_PENDING_KEY) === '1';
  } catch (error) {
    console.warn('Could not read the menu refresh state.', error);
    return false;
  }
}

function setMenuRefreshPending(pending: boolean): void {
  try {
    if (pending) sessionStorage.setItem(REFRESH_PENDING_KEY, '1');
    else sessionStorage.removeItem(REFRESH_PENDING_KEY);
  } catch (error) {
    console.warn('Could not store the menu refresh state.', error);
  }
}

/**
 * The user's own menu as the server builds it: GET /auth/menu, the same role-filtered, company-ordered
 * list login stores. (Not /auth/session: that route only exists when the SSO flag is on.)
 */
export async function fetchMenuOperations(): Promise<unknown> {
  const response = await BaseAPI.request('GET', '/auth/menu');
  if (response?.status !== 200) {
    throw new Error(`GET /auth/menu answered ${response?.status ?? 'nothing'}`);
  }
  return response.data?.operations;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Makes the left menu follow a save or reset without a new login. The menu the sidebar holds in
 * session storage (`operations`) is filtered to the user's roles, so it is replaced with the one the
 * server builds for this user, not rebuilt here from the company-wide order: that view does not know
 * which items this user can reach, and cannot bring back a group the server left out for them.
 *
 * Never throws: the order is already saved on the server. A failed fetch is retried after each delay in
 * `retryDelaysMs`. If every try fails the sidebar keeps its old menu, the refresh is marked pending
 * (see isMenuRefreshPending) and false is returned; the next successful call clears the mark.
 */
export async function syncSidebarMenu(
  fetchOperations: () => Promise<unknown> = fetchMenuOperations,
  retryDelaysMs: number[] = RETRY_DELAYS_MS,
  sleep: (ms: number) => Promise<void> = wait
): Promise<boolean> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    if (attempt > 0) await sleep(retryDelaysMs[attempt - 1]);
    try {
      const operations = await fetchOperations();
      if (!Array.isArray(operations)) {
        throw new Error('the session answer has no menu');
      }
      sessionStorage.setItem('operations', JSON.stringify(operations));
      setMenuRefreshPending(false);
      window.dispatchEvent(new Event(MENU_ORDER_CHANGED_EVENT));
      return true;
    } catch (error) {
      lastError = error;
    }
  }

  console.warn('Menu order saved, but the sidebar could not be refreshed. It is marked out of date until a refresh works.', lastError);
  setMenuRefreshPending(true);
  window.dispatchEvent(new Event(MENU_ORDER_CHANGED_EVENT));
  return false;
}
